import React, { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useFeatureFlagsContext } from '@/contexts/FeatureFlagsContext';
import { apiClient, ACCESS_LEVELS } from '@rti/shared';

interface ProtectedRouteProps {
  requireAdmin?: boolean;
  requireEmployee?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ requireAdmin = false, requireEmployee = false }) => {
  const { isAuthenticated, user, isLoading, isTrialExpired, isSubscriptionLapsed } = useAuth();
  const { getFlag, isLoading: isFeatureFlagsLoading } = useFeatureFlagsContext();
  const location = useLocation();

  useEffect(() => {
    if (!isAuthenticated || isLoading) return;
    apiClient.getUserData().catch((error) => {
      if (error?.response?.status === 404) {
        localStorage.removeItem("access_token");
        localStorage.removeItem("user");
        window.location.href = "/authorization";
      }
    });
  }, [location.pathname, isAuthenticated, isLoading]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="spinner text-4xl">⏳</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/authorization" replace state={{ from: location.pathname + location.search + location.hash }} />;
  }

  if (!isFeatureFlagsLoading && (isTrialExpired || isSubscriptionLapsed)) {
    const upgradePath = getFlag('enable_billing') ? '/billing' : '/dashboard';
    if (location.pathname !== upgradePath) {
      return <Navigate to={upgradePath} replace />;
    }
  }

  if (requireAdmin && !user?.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  if (requireEmployee && user?.effective_membership_id !== ACCESS_LEVELS.EMPLOYEE) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Outlet />;
};

export default ProtectedRoute;
